import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { workspaceApi } from '@/lib/api'
import { queryClient } from '@/lib/query-client'
import { useToast } from '@/stores/toast-store'
import { Skeleton } from '@/components/ui/skeleton'
import type { WorkspaceVisibility } from '@/types/workspace'

export default function WorkspaceSettingsPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data, isLoading } = useQuery({
    queryKey: ['workspace', slug],
    queryFn: () => workspaceApi.getWorkspace(slug!),
    enabled: !!slug,
  })

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState<WorkspaceVisibility>('private')

  useEffect(() => {
    if (data) {
      setName(data.name)
      setDescription(data.description ?? '')
      setVisibility(data.visibility)
    }
  }, [data])

  const updateMutation = useMutation({
    mutationFn: (values: { name?: string; description?: string; visibility?: WorkspaceVisibility }) =>
      workspaceApi.updateWorkspace(slug!, values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workspace', slug] })
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      toast({ title: 'Perubahan disimpan' })
    },
    onError: (err) => {
      toast({
        title: 'Gagal menyimpan perubahan',
        description: (err as Error).message,
        variant: 'destructive',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => workspaceApi.deleteWorkspace(slug!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      toast({ title: 'Workspace berhasil dihapus' })
      navigate('/workspaces', { replace: true })
    },
    onError: (err) => {
      toast({
        title: 'Gagal menghapus workspace',
        description: (err as Error).message,
        variant: 'destructive',
      })
    },
  })

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    updateMutation.mutate({ name, description: description || undefined, visibility })
  }

  function handleDelete() {
    if (!confirm(`Hapus workspace "${data?.name}"? Tindakan ini tidak dapat dibatalkan.`)) return
    deleteMutation.mutate()
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Pengaturan Workspace</h1>
        <p className="text-sm text-muted-foreground">Perbarui informasi workspace kamu.</p>
      </div>

      {/* Update form */}
      <form onSubmit={handleSave} className="max-w-lg rounded-xl border border-border bg-card p-6 space-y-5">
        <div className="space-y-1.5">
          <label htmlFor="name" className="text-sm font-medium">Nama</label>
          <input
            id="name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="description" className="text-sm font-medium">Deskripsi</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="visibility" className="text-sm font-medium">Visibilitas</label>
          <select
            id="visibility"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as WorkspaceVisibility)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="private">Privat</option>
            <option value="invite_only">Invite Only</option>
            <option value="public">Publik</option>
          </select>
        </div>

        <button
          type="submit"
          disabled={updateMutation.isPending}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          {updateMutation.isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
        </button>
      </form>

      {/* Danger zone */}
      <div className="max-w-lg rounded-xl border border-destructive/30 p-4 space-y-3">
        <h2 className="text-sm font-semibold text-destructive">Zona Berbahaya</h2>
        <p className="text-sm text-muted-foreground">
          Menghapus workspace akan menghapus semua data yang terkait secara permanen.
        </p>
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
          className="rounded-md border border-destructive px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
        >
          {deleteMutation.isPending ? 'Menghapus...' : 'Hapus Workspace'}
        </button>
      </div>
    </div>
  )
}
